import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import {
  Breadcrumb,
  Bullets,
  FixedBtns,
  FlexBox,
  HrTag,
  LazyImg,
  Typography,
  ViewMore
} from '@quikr/ui';
import JobsTag from '@quikr/ui/src/common/JobsTag';
import SEOtags from '@quikr/ui/src/common/SeoTag';
import SlideTransition from '@quikr/ui/src/common/Transitions/SlideTransition';
import {
  convertPriceToIndFormat,
  getLocalStorage,
  getSessionStorage,
  getWhatsappHref,
  returnUsableUser
} from '@quikr/ui/src/helpers/common';
import { Check } from '@quikr/ui/src/icons';
import { adsRHS } from '../../../googleAdConfig/jobs';
import { getApplicationsStatus, getRecruiterDetails, getStep2Data } from '../../../gql/apply';
import { getAction, removeHash } from '../../../helpers/action';
import { useGa, useTimeout } from '../../../hooks';
import VapExtrainfo from '../VapExtrainfo';
import BewareOfScammers from './BewareOfScammers';
import StickyHeader from './StickyHeader';

const VapLoadables = loadable(() => import('../VapLoadables'), { ssr: false });
const TrustedBadges = loadable(() => import('@quikr/ui/src/common/TrustedBadges'), { ssr: false });
const JobsApply = loadable(() => import('../JobsApply'), { ssr: false });
const AndroidDownloadPopup = loadable(() => import('../AndroidDownloadPopup'), { ssr: false });
const LeadPopup = loadable(() => import('../LeadPopup'), { ssr: false });
const GoogleAd = loadable(() => import('@quikr/ui/src/common/GoogleAd'), { ssr: false });

const BewareSectionMSite = styled(BewareOfScammers)`
  display: none;
  @media screen and (max-width: 767px) {
    display: flex;
    padding: 16px;
    text-align: center;

    svg {
      display: none;
    }
  }
`;

const BewareSectionDesktop = styled(BewareOfScammers)`
  display: none;
  @media screen and (min-width: 768px) {
    display: flex;
  }
`;

const JobsVAPWrapper = styled.div`
  @media screen and (max-width: 767px) {
    padding-top: 16px;
  }
`;

const JobsBullets = styled.div`
  flex-direction: row;
  align-items: center;
  ul {
    margin-bottom: 0;
  }
  @media screen and (max-width: 767px) {
    align-items: baseline;
    flex-direction: column;
    ul {
      margin-top: 10px;
    }
    li {
      margin-top: 0;
      &:nth-child(3) {
        display: none;
      }
    }
  }
`;

const ActionWrapper = styled.div`
  top: 45px;
`;

const VapTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.TextAlpha};
  margin: 16px 0 12px 0;
  &::first-letter {
    text-transform: uppercase;
  }
`;

const Li = styled(Typography)`
  color: ${({ theme }) => theme.colors.TextBeta};
  margin-right: 5px;
  &::before {
    content: '|';
    color: ${({ theme }) => theme.colors.Neutral[100]};
    margin-right: 5px;
  }
  &:first-child {
    &:before {
      display: none;
    }
  }
  @media screen and (min-width: 768px) {
    &::before {
      margin: 0 10px;
    }
  }
`;

const PriceWrapper = styled.div`
  @media (max-width: 767px) {
    padding: 15px 0 10px;
    display: flex;
    align-items: center;
  }
`;

const PriceDiv = styled.div`
  margin: 20px 0 30px;
  @media (max-width: 767px) {
    margin: 0;
  }
`;

const Price = styled(Typography)`
  color: ${props => props.theme.colors.Primary};
  margin-right: 10px;
`;

const MBlock = styled.div`
  display: inline;
  @media (max-width: 767px) {
    display: block;
  }
`;

const BtnWrapper = styled.div`
  margin-bottom: 32px;
  @media (max-width: 767px) {
    margin-bottom: 0;
    margin-left: auto;
  }
`;

const AdSecCss = css`
  padding: 40px 0 0;
  text-align: right;
  ${({ isSticky }) =>
    isSticky &&
    css`
      position: sticky;
      top: 115px;
      z-index: 1;
    `};
`;

const GoogleAdSec = styled(GoogleAd)`
  ${AdSecCss}
`;

const ApplySuccess = styled.div`
  color: ${({ theme }) => theme.colors.Secondary};
  margin-left: 25px;
  span {
    flex: 0 0 23px;
    height: 23px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => theme.colors.Secondary};
    margin-right: 8px;
  }
  svg {
    fill: ${({ theme }) => theme.colors.Secondary};
    width: 16px;
  }
  @media (max-width: 767px) {
    margin: 0;
    width: 100%;
    span {
      flex: 0 0 20px;
      height: 20px;
    }
    svg {
      width: 14px;
    }
  }
`;

const ImgWrapper = styled.span`
  margin-left: 20px;
  @media (max-width: 767px) {
    margin: 10px 0 0;
    display: block;
    height: 42px;
  }
`;

const CompanyImg = styled(LazyImg)`
  max-height: 42px;
`;

const Hr = styled(HrTag)`
  margin-bottom: 0;
`;

const stepStatus = {
  NOT_APPLIED: {
    action: 'APPLY'
  },
  STEP_ONE: {
    action: 'COMPLETED_STEPONE'
  },
  STEP_TWO: {
    action: 'COMPLETED_STEPTWO'
  }
};

const hashMapping = {
  contact: 'JOBS_CALL',
  apply: 'APPLY'
};

const androidDownloadPopupConfig = {
  strategy1CityIds: [531001, 142222],
  strategy2CityIds: [162222, 152222],
  strategy3CityIds: [29, 26, 22]
};

const VapJobs = ({ data: { ad: adData, isMobile, isAndroid, user, abRandValue } = {} }) => {
  const {
    highlights,
    images = [],
    adstyle,
    title,
    actions,
    views,
    timeago,
    city,
    id,
    user: adOwner,
    cat,
    subcat,
    breadcrumbs = [],
    minsalary,
    maxsalary,
    postedby,
    localities,
    externalredirecturl,
    websiteurl,
    subroles,
    trustedBadges,
    attr,
    createdtime,
    expiretime,
    stateregion,
    meta: { description = '' }
  } = adData;
  const role = attr.filter(option => option.key === 'Role')[0].value || '';
  const redirectionUrl = externalredirecturl || null;

  const compRef = useRef(null);
  const [ctaToShow, setctaToShow] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [androidDownloadPopup, setAndroidDownloadPopup] = useState({
    commonRedirectionGA: () => {},
    show: false,
    redirectOnClose: false,
    redirectUrl: ''
  });
  const [showCustomViewForJobsApply, setShowCustomViewForJobsApply] = useState({
    show: false,
    view: ''
  });
  const [showLeadPopup, setShowLeadPopup] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(true);
  const [applicationState, setApplicationState] = useState(stepStatus.NOT_APPLIED);
  const [userJobStatus, setUserJobStatus] = useState({});
  const [step2State, setStep2State] = useState(false);
  const [userObj, setUser] = useState(user && user.user);
  const [applySuccess, setApplySuccess] = useState(false);
  const [formType, setFormType] = useState(null);
  const [defaultView, setDefaultView] = useState('');
  const [expCalled, setExpCalled] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const [getStep2Details] = useMutation(getStep2Data);
  const [getRecruiter] = useMutation(getRecruiterDetails);
  const { TriggerGaEvent } = useGa();
  const utmParams = useRef();

  useEffect(() => {
    setShowBadges(true);
    const threshold = [0.85];
    const observer = new IntersectionObserver(
      ([entry]) => {
        setctaToShow(!entry.isIntersecting);
      },
      { threshold }
    );
    document.body.classList.add('darkHeader');
    compRef.current && observer.observe(compRef.current);
    return () => {
      observer.disconnect();
      document.body.classList.remove('darkHeader');
    };
  }, []);

  useEffect(() => {
    const value = getLocalStorage('__uo');
    if (value && (!user || (user && Object.keys(user).length < 1))) {
      setUser(value);
    }
  }, [user]);

  const showAutoFilledApplyForm = () => {
    if (!(userObj && userObj.id && applicationState === stepStatus.NOT_APPLIED && !showApply)) {
      return;
    }
    setFormType('APPLY');
    setShowApplyModal(true);
    setShowApply(true);
  };

  useTimeout(showAutoFilledApplyForm, 45000);

  const applicationPayload = {
    ads: [
      {
        id: parseInt(id),
        role
      }
    ],
    user: returnUsableUser(userObj)
  };

  const { loading, data: response } = useQuery(getApplicationsStatus, {
    variables: { payload: applicationPayload },
    skip: !userObj || (userObj && Object.keys(userObj).length < 1)
  });

  const updateCta = (acc, state, step, ctaToReplace = 'APPLY') => {
    const index = acc.indexOf(ctaToReplace);
    if (index > -1) {
      if (state === 'STEP_TWO') {
        acc.splice(index, 1);
      } else {
        acc[index] = step[state].action;
      }
    }
    return acc;
  };

  useEffect(() => {
    if (!loading && response && response.getApplicationsInformation) {
      const state = response.getApplicationsInformation.applications[0]
        ? response.getApplicationsInformation.applications[0].step
        : 'NOT_APPLIED';
      if (['STEP_ONE', 'STEP_TWO'].includes(state)) {
        setApplySuccess(true);
        actions.top = updateCta(actions.top, state, stepStatus);
        actions.bottom = updateCta(actions.bottom, state, stepStatus);
      }
      setApplicationState(stepStatus[state]);
      setUserJobStatus(response.getApplicationsInformation);
    }
  }, [loading, response, id]);

  const location = useLocation() || {};
  const searchParams = new URLSearchParams(location.search);

  useEffect(() => {
    let hash = getAction();
    if ((hash || searchParams.get('utm_source') === 'google_jobs_apply') && !expCalled) {
      if (!userObj && getLocalStorage('__uo')) {
        return;
      }
      if ((userObj && Object.keys(userJobStatus).length > 0) || !userObj) {
        setExpCalled(true);
        hash = hash || 'apply';
        if (hashMapping[hash] && actions.top.includes(hashMapping[hash])) {
          handleActionClick(hashMapping[hash]);
        } else {
          removeHash();
        }
      }
    }
    const params = {};
    for (const item of ['utm_source', 'utm_medium', 'utm_campaign']) {
      if (searchParams.has(item)) {
        const val = searchParams.get(item);
        if (val) {
          params[item] = encodeURIComponent(val);
        }
      }
    }
    utmParams.current = Object.keys(params).length > 0 ? params : getSessionStorage('utm_params');
  }, [userJobStatus, userObj, searchParams, expCalled]);

  const renderImages = images => {
    const imagesHTML = [];
    images.forEach((img, index) => {
      imagesHTML.push(<CompanyImg key={index} src={img.sm} alt="" />);
    });
    return imagesHTML;
  };

  const fireVAPGa = eventLabel => {
    const gaEventobj = {
      category: 'VAP',
      label: eventLabel,
      cityName: city.name,
      catName: cat.name,
      subcatName: subcat.name
    };

    TriggerGaEvent(gaEventobj);
  };

  const checkAndroidDownloadPopup = () => {
    const adCityId = city && city.id ? parseInt(city.id) : null;
    let showAndroidDownloadPopup = false;

    if (isAndroid) {
      if (
        androidDownloadPopupConfig.strategy1CityIds.includes(adCityId) ||
        androidDownloadPopupConfig.strategy1CityIds.includes(0)
      ) {
        showAndroidDownloadPopup = true;
      } else if (
        userObj &&
        (androidDownloadPopupConfig.strategy2CityIds.includes(adCityId) ||
          androidDownloadPopupConfig.strategy2CityIds.includes(0))
      ) {
        showAndroidDownloadPopup = true;
      }
    }

    return showAndroidDownloadPopup;
  };

  const checkLeadPopup = () => {
    const adCityId = city && city.id ? parseInt(city.id) : null;
    let showLeadPopup = false;
    if (isAndroid) {
      if (
        androidDownloadPopupConfig.strategy3CityIds.includes(adCityId) ||
        androidDownloadPopupConfig.strategy3CityIds.includes(0)
      ) {
        showLeadPopup = true;
      }
    }
    return showLeadPopup;
  };

  const showAndroidDownloadPopupForJobs = () => {
    setAndroidDownloadPopup({
      commonRedirectionGA: () => fireVAPGa('QJ_VAP_APPLY_REDIRECT'),
      show: true,
      redirectOnClose: true,
      redirectUrl:
        'https://play.google.com/store/apps/details?id=com.quikr.job&utm_source=QJ_Msite_Popup&utm_campaign=QJ_VAP_REDIRECT&utm_medium=apply'
    });
  };

  const handleActionClick = action => {
    // Redirect to playstore app
    if (
      isAndroid &&
      abRandValue <= 70 &&
      ['APPLY', 'COMPLETED_STEPONE', 'JOBS_CALL'].includes(action)
    ) {
      // if job profile exists apply step one and show banner
      if (
        action === 'APPLY' &&
        response &&
        response.getApplicationsInformation &&
        response.getApplicationsInformation.profile
      ) {
        setFormType('APPLY');
        setShowApply(true);
        setShowApplyModal(false);
        setShowCustomViewForJobsApply({
          show: true,
          view: () => {
            // update states associated with apply
            setApplicationState(stepStatus.STEP_ONE);
            const state = 'STEP_ONE';
            actions.top = updateCta(actions.top, state, stepStatus);
            actions.bottom = updateCta(actions.bottom, state, stepStatus);
            setApplySuccess(true);
            showAndroidDownloadPopupForJobs();
          }
        });
        return;
      }
      showAndroidDownloadPopupForJobs();
      return;
    }

    if (checkLeadPopup()) {
      setShowLeadPopup(true);
      return;
    }

    if (checkAndroidDownloadPopup()) {
      setAndroidDownloadPopup({ show: true });
      return;
    }

    if (
      userJobStatus &&
      (userJobStatus.quick_apply_applicable || userJobStatus.skip_apply_applicable)
    ) {
      const today = new Date().getTime();
      const updatedDate = new Date(userJobStatus.last_applied_date).getTime();
      const daysDiff = (updatedDate - today) / (1000 * 60 * 60 * 24);
      if (daysDiff > 7) {
        setShowApplyModal(true);
      } else {
        setShowApplyModal(false);
      }
    }

    switch (action) {
      case 'APPLY':
        setFormType('APPLY');
        toggleApplyForm();
        break;
      case 'COMPLETED_STEPONE':
        setFormType('APPLY');
        setShowApplyModal(true);
        handleCompletedStep('APPLY');
        break;
      case 'JOBS_CALL':
        setFormType('CALL');
        if (applicationState.action !== 'APPLY') {
          setShowApplyModal(true);
          handleCompletedStep('CALL');
        } else {
          toggleApplyForm();
        }
        break;
      case 'JOBS_WHATS_APP':
        setFormType('WHATSAPP');
        if (applicationState.action !== 'APPLY') {
          setShowApplyModal(true);
          handleCompletedStep('WHATSAPP');
        } else {
          toggleApplyForm();
        }
        break;
      default:
        break;
    }
  };

  const handleCompletedStep = async type => {
    const payload = {
      ad: {
        id: parseInt(id)
      },
      user: returnUsableUser(userObj)
    };
    if (type === 'APPLY') {
      payload.ad.role = role;
      const { data } = await getStep2Details({
        variables: { createPayload: payload }
      });
      if (data && data.getStep2Details && data.getStep2Details.step_2_form) {
        setStep2State(data.getStep2Details);
        toggleApplyForm();
      }
    } else {
      payload.ad.user = { id: +adOwner.id };
      const { data } = await getRecruiter({
        variables: { payload }
      });
      if (data && data.viewRecruiterContact) {
        setStep2State({ recruiter: data.viewRecruiterContact });
        if (data.viewRecruiterContact.error && data.viewRecruiterContact.error.code === 8118) {
          setDefaultView('payment');
        } else if (type === 'WHATSAPP') {
          setShowApplyModal(false);
          const url = getWhatsappHref(
            data.viewRecruiterContact.mobile,
            document.title.substring(0, document.title.indexOf('|'))
          );
          window.open(url, '_blank');
        } else {
          setDefaultView('viewContact');
        }
        toggleApplyForm();
      }
    }
  };

  const redirectToSnb = () => {
    setTimeout(() => {
      if (breadcrumbs && breadcrumbs[3]) {
        window.location = breadcrumbs[3].href;
      }
    }, 2000);
  };

  const toggleApplyForm = (applySuccess, actionState = {}) => {
    setShowApply(!showApply);

    if (actionState.action === stepStatus.NOT_APPLIED.action) return;

    if (
      (actionState.action === 'COMPLETED_STEPONE' || actionState.action === 'APPLY') &&
      actionState.status
    ) {
      setApplicationState(stepStatus.STEP_ONE);
      const state = 'STEP_TWO';
      const replace = actions.top.includes(actionState.action) ? actionState.action : 'APPLY';
      actions.top = updateCta(actions.top, state, stepStatus, replace);
      actions.bottom = updateCta(actions.bottom, state, stepStatus, replace);
    }
    if (applySuccess) {
      setApplySuccess(true);
      if (applicationState.action === 'APPLY' && !actionState.status) {
        setApplicationState(stepStatus.STEP_ONE);
        actions.top = updateCta(actions.top, 'STEP_ONE', stepStatus, 'APPLY');
        actions.bottom = updateCta(actions.bottom, 'STEP_ONE', stepStatus, 'APPLY');
      }
      if (showApply) {
        redirectToSnb();
      }
    }
  };

  const minExpAttr = attr.filter(item => item.key === 'Min_Experience');
  const maxExpAttr = attr.filter(item => item.key === 'Max_Experience');
  const hiringCompanyNameAttr = attr.filter(
    item => item.key === 'Hiring_Company_Name' && item.value !== 'Individual'
  );
  const companyNameAttr = attr.filter(item => item.key === 'Company');
  const employmentTypeMapper = {
    'Work From Home': 'PART_TIME',
    'Full Time Jobs': 'FULL_TIME',
    'Part Time Jobs': 'PART_TIME',
    Internships: 'INTERN',
    'Work Abroad': 'FULL_TIME'
  };
  const educationArray = attr.filter(item => item.key === 'Education');
  let seoEducationValue = null;
  if (educationArray[0]) {
    const education = educationArray[0].value;
    if (typeof education !== 'undefined' && education !== null) {
      const jobEducationValue = education.toLowerCase();
      if (jobEducationValue.startsWith('postgraduate')) {
        seoEducationValue = 'postgraduate degree';
      } else if (jobEducationValue.startsWith('graduat')) {
        seoEducationValue = 'bachelor degree';
      } else if (jobEducationValue.startsWith('diploma')) {
        seoEducationValue = 'associate degree';
      } else {
        seoEducationValue = 'high school';
      }
    }
  }

  const SEOdata = {
    adId: id,
    title,
    role,
    description,
    isWFHjob: subcat.name === 'Work From Home',
    minExp: minExpAttr[0] ? minExpAttr[0].value : '',
    maxExp: maxExpAttr[0] ? maxExpAttr[0].value : '',
    datePosted: createdtime,
    validThrough: expiretime,
    employmentType: employmentTypeMapper[subcat.name],
    company: hiringCompanyNameAttr[0]
      ? hiringCompanyNameAttr[0].value
      : companyNameAttr[0]
      ? companyNameAttr[0].value
      : postedby,
    cityName: city.name,
    stateName: stateregion.name,
    maxsalary,
    minsalary,
    education: seoEducationValue
  };

  const gutter = isMobile ? 12 : 20;
  return (
    <>
      <SEOtags type={'JobPosting'} seoData={SEOdata} VAP={true} />
      {androidDownloadPopup.show && (
        <AndroidDownloadPopup
          commonRedirectionGA={androidDownloadPopup.commonRedirectionGA}
          city={city}
          onClose={() => setAndroidDownloadPopup(prev => ({ ...prev, show: false }))}
          redirectOnClose={androidDownloadPopup.redirectOnClose}
          redirectUrl={androidDownloadPopup.redirectUrl}
          showPopup={androidDownloadPopup.show}
          fireGa={fireVAPGa}
        />
      )}
      {showLeadPopup && (
        <LeadPopup showPopup={setShowLeadPopup} fireGa={fireVAPGa} id={id} city={city} />
      )}
      <BewareSectionMSite />
      <FlexBox.Row margin={gutter}>
        <FlexBox.Column md={8} sm={8} xs={12} gutterWidth={gutter}>
          {!isMobile && (
            <StickyHeader
              minsalary={minsalary}
              maxsalary={maxsalary}
              title={title}
              type="top"
              actions={actions.top}
              actionClick={handleActionClick}
              active={ctaToShow}
              applySuccess={applySuccess}
              fireGa={fireVAPGa}
            />
          )}
          {!isMobile && <Breadcrumb data={breadcrumbs} />}
          <JobsVAPWrapper className="pR">
            <ActionWrapper className="pA r0">{/* <Share /> */}</ActionWrapper>
            <JobsBullets className="dF fwW">
              {adstyle !== 'BASIC' && <JobsTag type={adstyle} />}
              {highlights && highlights.length > 0 && <Bullets data={highlights} />}
            </JobsBullets>
            <VapTitle tagName="h1" modifiers="h1">
              <ViewMore content={title} maxLength={isMobile ? 68 : 133} />
            </VapTitle>
            <ul className="dF fwW">
              {city && city.name && (
                <Li tagName="li" modifiers={isMobile ? 'bt2' : ''} title={city.name}>
                  {city.name}
                </Li>
              )}
              {views && (
                <Li tagName="li" modifiers={isMobile ? 'bt2' : ''}>
                  {views + ' views'}
                </Li>
              )}
              {postedby && (
                <Li tagName="li" modifiers={isMobile ? 'bt2' : ''}>
                  {'Posted by: ' + postedby}
                </Li>
              )}
              {timeago && (
                <Li tagName="li" modifiers={isMobile ? 'bt2' : ''}>
                  {timeago}
                </Li>
              )}
            </ul>
            {isMobile && images && images.length > 0 && (
              <ImgWrapper>{renderImages(images)}</ImgWrapper>
            )}
            {isMobile && showBadges && <TrustedBadges data={trustedBadges} />}
            <PriceWrapper ref={compRef}>
              <PriceDiv className="dFA f1">
                <Price tagName="label" modifiers={['h1', 'bold']}>
                  <span>₹</span>
                  {convertPriceToIndFormat(minsalary)}
                  &nbsp;-&nbsp;
                  <MBlock>
                    <span>₹</span>
                    {convertPriceToIndFormat(maxsalary)}
                  </MBlock>
                </Price>
                {!isMobile && images && images.length > 0 && (
                  <ImgWrapper>{renderImages(images)}</ImgWrapper>
                )}
                {!isMobile && showBadges && <TrustedBadges data={trustedBadges} />}
              </PriceDiv>
              <BtnWrapper className="dFA">
                <FixedBtns
                  type="top"
                  actions={actions.top}
                  actionClick={handleActionClick}
                  fireGa={fireVAPGa}
                />
                {!isMobile && applySuccess && (
                  <ApplySuccess className="dFA f1">
                    <span className="dFA jcC">
                      <Check />
                    </span>{' '}
                    Applied
                  </ApplySuccess>
                )}
              </BtnWrapper>
            </PriceWrapper>
            <BewareSectionDesktop />
            {isMobile && applySuccess && (
              <ApplySuccess className="dFA f1 jcFE">
                <span className="dFA jcC">
                  <Check />
                </span>{' '}
                Applied
              </ApplySuccess>
            )}
            <VapExtrainfo
              data={adData}
              localities={localities}
              websiteurl={websiteurl}
              subroles={subroles}
              type="jobs"
              top={159}
              positionY={229}
            />
            {!isMobile && <Hr />}
            <VapLoadables
              type="Primary"
              adData={adData}
              userId={String(adData.user.id)}
              fireGa={fireVAPGa}
            />
          </JobsVAPWrapper>
        </FlexBox.Column>
        {!isMobile && (
          <FlexBox.Column md={4} sm={4} gutterWidth={gutter}>
            {adsRHS.map((each, idx) => {
              return <GoogleAdSec key={each.name} config={each} isSticky={each.isSticky} />;
            })}
          </FlexBox.Column>
        )}
        {isMobile && (
          <SlideTransition in={isMobile} duration={300} appear>
            <FixedBtns
              type="bottom"
              actions={actions.bottom}
              actionClick={handleActionClick}
              fireGa={fireVAPGa}
            />
          </SlideTransition>
        )}
      </FlexBox.Row>
      {showApply && (
        <JobsApply
          action={applicationState.action}
          state={step2State}
          user={userObj}
          onClose={toggleApplyForm}
          city={subcat.id === '296' ? {} : city}
          id={id}
          role={role}
          isMobile={isMobile}
          formType={formType}
          defaultView={defaultView}
          redirectionUrl={redirectionUrl}
          adOwner={adOwner}
          userProfile={userJobStatus}
          showModal={showApplyModal}
          fireGa={fireVAPGa}
          utmParams={utmParams.current}
          showCustomViewForJobsApply={showCustomViewForJobsApply}
        />
      )}
    </>
  );
};

VapJobs.propTypes = {
  data: PropTypes.object,
  canonical: PropTypes.bool
};

VapJobs.defaultProps = {};

export default VapJobs;
